import React, { useState } from "react";
import Masonry from "react-masonry-css";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { useTranslation } from "../i18n";

// Import all photos from the assets/photos folder
const importAllPhotos = (r: any) => {
  return r.keys().map(r);
};

// Get all photo files from the photos directory
const photoContext = (require as any).context(
  "../assets/photos",
  false,
  /\.(jpeg|jpg|png|JPEG)$/
);
const allPhotos = importAllPhotos(photoContext);

// Function to randomize photos
const getRandomizedPhotos = (): string[] => {
  return [...allPhotos].sort(() => Math.random() - 0.5);
};

const Gallery: React.FC = () => {
  const { raw, t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  // State to store randomized photos - this will be different each time the component mounts
  const [randomizedPhotos] = useState(() => getRandomizedPhotos());
  const featureItems = raw<string[]>("gallery.features");

  const scrollToSection = (sectionId: string) => {
    // Navigate to home page with hash
    window.location.href = `/#${sectionId}`;
  };

  // Handle hash navigation when coming from other pages
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Breakpoint configuration for masonry layout
  const breakpointColumns = {
    default: 4,
    1100: 3,
    700: 2,
    500: 2
  };

  return (
    <div className="min-h-screen bg-[#f3ece3]">
      <Seo
        pageTitle={t("seo.galleryTitle")}
        description={t("seo.galleryDescription")}
      />
      <Navigation scrollToSection={scrollToSection} />

      <section className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.7),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(214,189,159,0.14),transparent_28%)] pt-6 pb-16">
        <div className="container mx-auto px-4 py-0">
          <div className="mx-auto max-w-7xl rounded-[2rem] border border-white/70 bg-[rgba(255,250,244,0.72)] p-4 shadow-[0_28px_90px_rgba(117,95,74,0.12)] backdrop-blur-xl sm:p-6">
            {/* Gallery Header */}
            <section className="rounded-[1.75rem] border border-[#eadbc8] bg-[#fffdf9] px-6 py-8 shadow-[0_24px_60px_rgba(117,95,74,0.10)] sm:px-8 sm:py-9">
              <div className="mx-auto max-w-3xl text-center">
                <h1 className="mb-4 text-base font-light tracking-[0.24em] text-gray-800 md:text-lg">
                  {t("gallery.title")}
                </h1>
                <div className="mx-auto mb-5 h-px w-20 bg-gray-400"></div>
                <p className="text-lg text-gray-600 font-light tracking-wide md:text-[1.15rem]">
                  {t("gallery.subtitle")}
                </p>
                <p className="mt-1.5 text-md italic font-light tracking-wide text-yellow-600 md:text-lg">
                  {t("gallery.handcrafted")}
                </p>
                <p className="mt-3 text-sm text-gray-500 md:text-[0.95rem]">
                  {t("gallery.designCount", { count: randomizedPhotos.length })}
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-500">
                  {featureItems.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </div>
            </section>

            {/* Gallery Grid */}
            <section className="px-1 py-8 sm:px-2 sm:py-10">
              <Masonry
                breakpointCols={breakpointColumns}
                className="flex gap-4"
                columnClassName="flex flex-col gap-4"
              >
                {randomizedPhotos.map((photo: string, index: number) => (
                  <div
                    key={index}
                    className="group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
                    onClick={() => setSelectedImage(photo)}
                  >
                    <img
                      src={photo}
                      alt={t("common.bridalDressAlt", { number: index + 1 })}
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                ))}
              </Masonry>
            </section>

            {/* Call to Action */}
            <section className="rounded-[1.75rem] border border-[#eadbc8] bg-[#fffdf9] px-6 py-12 shadow-[0_24px_60px_rgba(117,95,74,0.10)] sm:px-8">
              <div className="text-center">
                <h2 className="text-3xl font-light mb-6 text-gray-800">
                  {t("gallery.ctaTitle")}
                </h2>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/tailor-my-dress"
                    className="inline-flex items-center justify-center rounded-full bg-[#2f241d] px-8 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-white transition duration-300 hover:bg-[#43342a]"
                  >
                    {t("gallery.designButton")}
                  </a>
                  <a
                    href="/rental-consultation"
                    className="inline-flex items-center justify-center rounded-full border border-[#d8c7b2] bg-[#fffaf4]/82 px-8 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-stone-800 backdrop-blur-sm transition duration-300 hover:border-[#bea07a] hover:bg-[#fffdf9]"
                  >
                    {t("gallery.rentalButton")}
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            &times;
          </button>
          <img
            src={selectedImage}
            alt={t("common.selectedBridalDress")}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Gallery;

